import { Spinner } from '@finn/design-system/atoms/spinner';
import { useProductSwapAvailabilities } from '@finn/platform-modules';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import { FilterKey, GenericVehicleDetails } from '@finn/ua-vehicle';
import { useSession } from '@finn/ui-utils';
import { ReactNode, RefObject, useEffect, useState } from 'react';
import handleViewport from 'react-in-viewport';

export const SwapInfo = handleViewport<any, any>(
  ({
    product,
    term,
    children,
    enterCount,
    forwardedRef,
  }: {
    product?: GenericVehicleDetails;
    term?: string;
    children: ReactNode;
    // react-in-viewport injected props
    enterCount?: number;
    forwardedRef?: RefObject<HTMLDivElement>;
  }) => {
    const [session] = useSession();
    const isLoggedIn = !!session?.user?.email;

    // Client-side: safely access search params via window
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const subscriptionIdInUrl = params.get(FilterKey.SWAP_SUBSCRIPTION_ID);
      const firstActiveSubscriptionId = localStorage.getItem('finn_f_sub_id');
      const selectedActiveSubscription =
        localStorage.getItem('finn_sel_sub_id');
      if (subscriptionIdInUrl) {
        setSubscriptionId(subscriptionIdInUrl);
      } else if (selectedActiveSubscription) {
        setSubscriptionId(selectedActiveSubscription);
      } else if (firstActiveSubscriptionId) {
        setSubscriptionId(firstActiveSubscriptionId);
      }
    }, []);
    const isSwapInfoEnabled = useIsABVariant(Features.ExpSwappableInfo);

    // fetch availabilities when all conditions are met
    // 1. user needs to be logged in
    // 2. swap info feature flag needs to be enabled
    // 3. subscriptionId needs to be available
    // 4. enterCount should be greater than 0 to ensure the component is in the viewport to avoid unnecessary API calls
    const shouldFetchAvailabilities =
      isLoggedIn && isSwapInfoEnabled && !!subscriptionId && enterCount;

    const { data: swapAvailabilities, status } = useProductSwapAvailabilities(
      shouldFetchAvailabilities ? subscriptionId : null,
      product?.config_id?.toString?.(),
      term ? [term] : (product?.available_terms || []).map(String)
    );

    const isLoadingSwap = status === 'loading';
    const canSwap = Boolean(swapAvailabilities?.length);

    return isLoadingSwap ? (
      <Spinner className="size-6 fill-trustedBlue" />
    ) : (
      <div className="flex flex-col gap-1" ref={forwardedRef}>
        <span>{children}</span>
        {canSwap && (
          <span className="text-trustedBlue body-14-semibold">
            Tausch möglich
          </span>
        )}
      </div>
    );
  },
  { threshold: 0.3 },
  { disconnectOnLeave: true }
);
